import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import AdminBillingNav from '@/components/adminBilling/AdminBillingNav';
import StatusChip from '@/components/adminBilling/StatusChip';
import { adminBillingService, formatCents } from '@/api/services/adminBilling';
import { formatApiError } from '@/utils/formatApiError';
import { useMe } from '@/context/MeContext';
import { Toggle } from '@/components/ui';
import { DataTableScrollArea } from '@/components/table';

export default function AdminBillingSubscriptionsPage() {
  const { me } = useMe();
  const canManage = me?.permissions.canManageAdminBilling;
  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [provider, setProvider] = useState('');
  const [expiring, setExpiring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((nextCursor?: string | null) => {
    adminBillingService
      .subscriptions({
        provider: provider || undefined,
        expiring: expiring || undefined,
        cursor: nextCursor || undefined,
      })
      .then((res) => {
        setItems((prev) => (nextCursor ? [...prev, ...res.items] : res.items));
        setCursor(res.nextCursor ?? null);
        setHasMore(res.hasMore);
      })
      .catch((e) => setError(formatApiError(e)));
  }, [provider, expiring]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="admin-billing-page">
      <AdminBillingNav />
      <PageHeader title="Subscriptions" subtitle="Organization entitlements and subscription state." />
      {error ? <div className="alert alert-error">{error}</div> : null}
      <section className="panel">
        <div className="table-action-bar">
          <select value={provider} onChange={(e) => setProvider(e.target.value)}>
            <option value="">All providers</option>
            <option value="stripe">Stripe</option>
            <option value="manual">Manual</option>
            <option value="internal">Internal</option>
          </select>
          <Toggle
            compact
            checked={expiring}
            onChange={setExpiring}
            label="Expiring soon"
          />
        </div>
        <DataTableScrollArea>
        <table className="data-table">
          <thead>
            <tr>
              <th>Organization</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Provider</th>
              <th>Expires</th>
              <th>Amount</th>
              <th>Owner</th>
              {canManage ? <th>Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={String(s.id)}>
                <td>
                  <Link to={`/admin/billing/organizations/${s.organizationId}`}>
                    {String(s.organizationName || s.organizationSlug)}
                  </Link>
                </td>
                <td>{String(s.planSlug || '—')}</td>
                <td><StatusChip status={String(s.lifecycleState || s.status)} /></td>
                <td>{String(s.provider)}</td>
                <td>{s.expiresAt ? new Date(String(s.expiresAt)).toLocaleDateString() : '—'}</td>
                <td>{formatCents(Number(s.amountCents), String(s.currency || 'EUR'))}</td>
                <td>{String(s.ownerEmail || '—')}</td>
                {canManage ? (
                  <td>
                    <Link to={`/admin/billing/organizations/${s.organizationId}`}>Manage</Link>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
        </DataTableScrollArea>
        {hasMore && cursor ? (
          <button type="button" className="btn btn-secondary" onClick={() => load(cursor)}>
            Load more
          </button>
        ) : null}
      </section>
    </div>
  );
}
